import React, { FC, useContext } from "react";

import CloseIcon from "@mui/icons-material/Close";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";

import { AdminProtected } from "@/components/Application/AdminProtected";
import { Option } from "@/components/Application/HtmlElements";
import { Translate } from "@/components/Application/Translate";
import { ProcedureCompetencyListItem } from "@/components/Procedures/ProcedureCompetency/ProcedureCompetencyListItem";
import { ProcedureNodeFolderItem } from "@/components/Procedures/ProcedureNodeFolders/ProcedureNodeFolderItem";
import { ProcedureProjectOwnerListItem } from "@/components/Procedures/ProcedureProjectOwners/ProcedureProjectOwnerListItem";
import { NewProcedureValidation } from "@/components/Procedures/Validation/NewProcedureValidation";

import { AlertRequest } from "@/helpers/alerts/AlertRequest";
import CustomInput from "@/helpers/components/CustomFormFields/CustomInput";
import CustomSelect from "@/helpers/components/CustomFormFields/CustomSelect";
import CustomSelectCheckbox from "@/helpers/components/CustomFormFields/CustomSelectCheckbox";
import { Spinner } from "@/helpers/components/Spinner";

import { CreateNewProcedureRequest } from "@/model/Procedure/CreateNewProcedure";
import { User } from "@/model/User/User";
import { UserContext } from "@/model/User/UserContext";

import { useFetchGroups } from "@/service/GroupQueries";
import { useMutateCreateNewProcedure } from "@/service/ProcedureMutations";
import {
  useFetchActiveProcedureGroupFolders,
  useFetchProcedureMetaData,
} from "@/service/ProcedureQueries";

interface NewProcedureFormProps {
  user: User | null;
  open: boolean;             // kept for parity with parent, not used directly here
  handleClose: () => void;
}

export const NewProcedureForm: FC<NewProcedureFormProps> = (props) => {
  const { handleClose } = props;
  const user = useContext(UserContext);

  const { data: procedureMetaData, isLoading } = useFetchProcedureMetaData("?");
  const { groupList } = useFetchActiveProcedureGroupFolders();
  const { createProcedure } = useMutateCreateNewProcedure();
  const { data: groups, isLoading: isLoadingGroups } = useFetchGroups();

  const handleSubmit = async (values: CreateNewProcedureRequest) => {
    try {
      const responsible = procedureMetaData?.ProjectOwners.find(
        (owner) => +owner.UserId === +values.Responsible.Id,
      );
      if (responsible) {
        values.Responsible.Name = responsible.Name;
      }
      await createProcedure(values);
      AlertRequest.success();
    } catch (error) {
      AlertRequest.failed();
      console.error(error);
    } finally {
      handleClose();
    }
  };

  if (isLoading || isLoadingGroups || !procedureMetaData || !groups) {
    return <Spinner text="Loading procedure data" />;
  }

  const meAsOwner = procedureMetaData?.ProjectOwners.find(
    (owner) => owner.UserId === user?.Id,
  );

  const isLink = (folderId: number) =>
    !!groupList.find((g) => g.Id === folderId && g.Link);

  const initialValues: CreateNewProcedureRequest = {
    ProjectGroup: -10,
    Name: "",
    Responsible: {
      Id: meAsOwner?.UserId ?? procedureMetaData.ProjectOwners[0]?.UserId,
      Name: meAsOwner?.Name ?? procedureMetaData.ProjectOwners[0]?.Name,
    },
    TrainingCompetency: -1,
    GroupsWithCompetencyRequirement: [],
  };

  return (
    <AdminProtected>
      <Box className="wrapper-left" sx={{ position: "relative" }}>
        {/* Title */}
        <DialogTitle
          id="new-procedure-title"
          sx={{
            pr: 6,
            pb: 0.5,
            fontWeight: 800,
          }}
        >
          <Translate
            HTMLTag="span"
            className="heading"
            String="Procedures.NewProcedure.CreateNewProcedure"
          >
            Create new procedure
          </Translate>
        </DialogTitle>

        {/* Close button */}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent
          dividers
          sx={{
            pt: 1.5,
          }}
        >
          {/* Subtitle */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <Translate String="Procedures.NewProcedure.Subtitle">
              Provide the basic details, select an owner and choose where this
              procedure will live.
            </Translate>
          </Typography>

          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={NewProcedureValidation}
            validateOnMount
          >
            {({ values, isValid, dirty }) => (
              <Form>
                <Grid container spacing={2}>
                  {/* Name */}
                  <Grid item xs={12} sm={6}>
                    <CustomInput
                      id="procedureName"
                      type="text"
                      label="Name"
                      name="Name"
                      translationString="Procedures.NewProcedure.Name"
                      placeholder="e.g. Start-up checklist for Line 3"
                      fullWidth
                      onKeyDown={(e) => e.stopPropagation()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <DescriptionOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ "& .MuiInputBase-root": { borderRadius: 2, height: 40 } }}
                    />
                  </Grid>

                  {/* Folder */}
                  <Grid item xs={12} sm={6}>
                    <CustomSelect
                      id="folder"
                      label="Folder"
                      name="ProjectGroup"
                      translationString="Procedures.NewProcedure.Folder"
                      fullWidth
                      sx={{ "& select": { height: 40 }, borderRadius: 2 }}
                    >
                      <Option
                        String="Procedures.NewProcedure.ChooseFolder"
                        value={-10}
                        key={-10}
                        disabled
                        selected
                      >
                        Velg mappe
                      </Option>

                      {procedureMetaData.Folders?.filter(
                        (folder) => folder.Name !== "[Root]" && !isLink(folder.GroupNumber),
                      ).map((folder) => (
                        <ProcedureNodeFolderItem
                          Folder={folder}
                          key={folder.GroupNumber.toString()}
                        />
                      ))}
                    </CustomSelect>
                    <Typography variant="caption" color="text.secondary">
                      <Translate String="Procedures.NewProcedure.Folder.Help">
                        This is where users will find the procedure.
                      </Translate>
                    </Typography>
                  </Grid>

                  {/* Owner */}
                  <Grid item xs={12} sm={6}>
                    <CustomSelect
                      id="Responsible.Id"
                      label="Owner"
                      name="Responsible.Id"
                      translationString="Procedures.NewProcedure.Owner"
                      fullWidth
                      sx={{ "& select": { height: 40 }, borderRadius: 2 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonOutlineOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      } as any}
                    >
                      {procedureMetaData.ProjectOwners?.map((owner, idx) => (
                        <ProcedureProjectOwnerListItem Owner={owner} key={idx} />
                      ))}
                    </CustomSelect>
                    <Typography variant="caption" color="text.secondary">
                      <Translate String="Procedures.NewProcedure.Owner.Help">
                        The responsible person for updates and accuracy.
                      </Translate>
                    </Typography>
                  </Grid>

                  {/* Competence */}
                  <Grid item xs={12} sm={6}>
                    <CustomSelect
                      id="competence"
                      label="Competence"
                      name="TrainingCompetency"
                      translationString="Procedures.NewProcedure.Competency"
                      fullWidth
                      sx={{ "& select": { height: 40 }, borderRadius: 2 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SchoolOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      } as any}
                    >
                      {procedureMetaData.Competency?.map((competence, idx) => (
                        <ProcedureCompetencyListItem Competence={competence} key={idx} />
                      ))}
                    </CustomSelect>
                    <Typography variant="caption" color="text.secondary">
                      <Translate String="Procedures.NewProcedure.Competency.Help">
                        Optional: link a training competency to this procedure.
                      </Translate>
                    </Typography>
                  </Grid>

                  {/* Groups with competence requirement */}
                  <Grid item xs={12}>
                    <CustomSelectCheckbox
                      id="GroupsWithCompetencyRequirement"
                      label="Groups with competence requirement"
                      name="GroupsWithCompetencyRequirement"
                      translationString="Training.NewProcedure.GroupsWithCompetenceRequirement"
                      options={
                        groups
                          .filter((x) => x.GroupModules.Competence)
                          .map((x) => ({ value: String(x.Id), label: x.Name })) || []
                      }
                      style={{ width: "100%" }}
                      disabled={
                        values.TrainingCompetency === -1 ||
                        values.TrainingCompetency === "0"
                      }
                    />
                    <Typography variant="caption" color="text.secondary">
                      <Translate String="Procedures.NewProcedure.GroupsWithCompetenceRequirement.Help">
                        Choose which groups must have this competence (only enabled if a
                        competence is selected).
                      </Translate>
                    </Typography>
                  </Grid>
                </Grid>

                {/* Footer */}
                <DialogActions
                  sx={{
                    px: 0,
                    pt: 2,
                    mt: 0.5,
                    borderTop: (t) => `1px solid ${t.palette.divider}`,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<CloseIcon />}
                    onClick={handleClose}
                    sx={{ height: 40, borderRadius: 2, textTransform: "none", fontWeight: 800 }}
                  >
                    <Translate String="Procedure.Button.Close">Close</Translate>
                  </Button>

                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<SaveIcon />}
                    type="submit"
                    disabled={!isValid || !dirty}
                    sx={{ height: 40, borderRadius: 2, textTransform: "none", fontWeight: 800 }}
                  >
                    <Translate String="Procedure.Button.Save">Save</Translate>
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </DialogContent>

        {/* Spacer (keep grid layout parity if needed) */}
        <div className="row-one-column row">&nbsp;</div>
      </Box>
    </AdminProtected>
  );
};
